<template>
	<div style="width: 100%">
		<div
			class="agenda-card-wrapper d-flex justify-space-between align-center px-3 pb-3"
			:style="wrapperStyle"
		>
			<div>
				<ProfileBadge
					v-if="agendaEvent.sponsored_by"
					isPin
					:pin="{ icon: sponsoredStarIcon, text: sponsoredLabel }"
				/>
				<div
					v-if="timeStatus"
					class="agenda-card-wrapper__time-start"
					:style="{ backgroundColor: timeStatus === 'now' ? '#002855' : '#0055a3' }"
				>
					{{ timeStatus === 'now' ? 'Now' : 'Soon' }}
				</div>
				<div class="d-flex align-start">
					<div
						class="agenda-card-date"
						v-bind:style="{ color: isAgendaPast ? '#AAA' : 'var(--accent-color)' }"
					>
						<div class="agenda-card-date__begin">
							{{
								formatTime(
									agendaEvent.events_agenda_from,
									agendaEvent.agendaTimezone
								)
							}}
						</div>
						<div class="agenda-card-date__end">
							{{
								formatTime(agendaEvent.events_agenda_to, agendaEvent.agendaTimezone)
							}}
						</div>
						<div class="agenda-card-date__end">
							({{ getTimezoneAbbreviation(agendaEvent.agendaTimezone) }})
						</div>
						<div v-if="tracksCount" class="agenda-card-date__circle-wrapper">
							<div
								v-for="i in tracksCount"
								:key="i"
								class="agenda-card-date__circle"
								:style="{ background: isAgendaPast ? '#AAA' : '#674ea6' }"
							/>
						</div>
					</div>
					<div class="ml-2">
						<div
							class="agenda-card-description mb-0"
							v-bind:style="{ color: isAgendaPast ? '#AAA' : 'var(--accent-color)' }"
						>
							{{ agendaEvent.events_agenda_text }}
						</div>
						<div
							class="agenda-card-location mt-2"
							v-if="agendaEvent.session_location"
							v-bind:style="{
								color: isAgendaPast ? '#AAA' : 'var(--location-color)',
							}"
						>
							<Icon
								type="pin"
								:color="isAgendaPast ? '#aaa' : 'var(--location-color)'"
								:size="12"
								:pr="4"
							/>
							{{ agendaEvent.session_location }}
						</div>
						<div
							v-if="
								agendaEvent &&
								agendaEvent.session_limited &&
								agendaEvent.session_register_link &&
								!isAgendaPast
							"
							class="agenda-card-seats"
						>
							Limited seats
						</div>
					</div>
				</div>
			</div>
			<div>
				<v-btn
					@click.prevent="toggleMyAgenda"
					class="agenda-card-schedule-btn elevation-0"
					:loading="
						isMyAgendaLoading && myAgendaIdIsLoading === agendaEvent.events_agenda_id
					"
				>
					<Icon :type="scheduleIcon.icon" :color="scheduleIcon.color" :size="24" />
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import ProfileBadge from '@/components/ProfileBadge.vue'

Vue.use(VueScrollTo)

export default {
	name: 'AgendaCard',
	components: {
		ProfileBadge,
	},
	props: {
		agendaEvent: {
			type: Object,
			default: () => ({}),
		},
		selectedButton: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			sponsoredStarIcon,
			isTimeStarted: false,
		}
	},
	methods: {
		...mapActions('event', ['addMyAgenda', 'removeMyAgenda']),
		formatTime(timestamp, timezone) {
			const options = {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: 'UTC',
			}

			const date = new Date(timestamp * 1000)
			const offset = Number(timezone.timezone_utc.slice(-2))
			const sign = timezone.timezone_utc.slice(3, 4)

			if (sign === '-') {
				date.setHours(date.getHours() - offset)
			} else {
				date.setHours(date.getHours() + offset)
			}

			return date.toLocaleTimeString('en-US', options)
		},

		getTimezoneAbbreviation(timezone) {
			return timezone.timezone_code || 'UTC'
		},

		toggleMyAgenda() {
			if (!this.isMyAgendaLoading) {
				if (this.agendaEvent.is_scheduled) {
					this.removeMyAgenda({
						id: this.agendaEvent.events_agenda_id,
						myAgenda: this.$route.name === 'my-agenda-events',
						single: false,
						filter: this.currentFilter,
					})
				} else {
					this.addMyAgenda({ id: this.agendaEvent.events_agenda_id, single: false })
				}
			}
		},
	},
	computed: {
		...mapGetters('event', ['isMyAgendaLoading', 'myAgendaIdIsLoading']),
		...mapGetters('settings', ['styles']),
		wrapperStyle() {
			return {
				paddingTop: this.agendaEvent.sponsored_by || this.timeStatus ? '32px' : '12px',
				borderColor: this.timeStatus ? '#0055a3' : '#e2e2e2',
			}
		},
		tracksCount() {
			if (this.agendaEvent && this.agendaEvent.tracks) {
				return Math.min(this.agendaEvent.tracks.length, 10)
			}
			return 0
		},
		timeStatus() {
			const now = new Date()

			const from = new Date(this.agendaEvent.events_agenda_from * 1000)
			const to = new Date(this.agendaEvent.events_agenda_to * 1000)
			const offset = Number(this.agendaEvent.agendaTimezone.timezone_utc.slice(-2))
			const sign = this.agendaEvent.agendaTimezone.timezone_utc.slice(3, 4)

			if (sign === '+') {
				from.setHours(from.getHours() + offset)
				to.setHours(to.getHours() + offset)
			} else {
				from.setHours(from.getHours() - offset)
				to.setHours(to.getHours() - offset)
			}

			const fifteenMinutesInMs = 15 * 60 * 1000

			if (now >= from && now <= to) {
				return 'now'
			} else if (now >= new Date(from.getTime() - fifteenMinutesInMs) && now < from) {
				return 'soon'
			}
			return ''
		},

		sponsoredLabel() {
			return this.agendaEvent.sponsored_by
				? `Sponsored by ${this.agendaEvent.sponsored_by}`
				: ''
		},
		scheduleIcon() {
			return this.agendaEvent.is_scheduled
				? {
						icon: 'remove_schedule',
						color: '#9B9B9B',
				  }
				: { icon: 'add_schedule', color: this.styles.button_main_background }
		},
		isAgendaPast() {
			const date = new Date()
			const currentGMTDate = date.getTime() + date.getTimezoneOffset() * 60 * 1000
			const agendaTimezoneOffset =
				+this.agendaEvent.agendaTimezone.timezone_utc.slice(-2) * 60 * 60 * 1000
			const agendaTimeZoneOffsetDirection =
				this.agendaEvent.agendaTimezone.timezone_utc.slice(3, 4) === '+'
			const agendaDate = new Date(this.agendaEvent.events_agenda_to * 1000).getTime()
			const agendaGMTDate =
				agendaDate +
				(agendaTimeZoneOffsetDirection === '+'
					? -agendaTimezoneOffset
					: +agendaTimezoneOffset)
			return currentGMTDate > agendaGMTDate
		},
		currentFilter() {
			const registeredFilter = this.selectedButton.some((button) => button === 'registered')
			const scheduledFilter = this.selectedButton.some((button) => button === 'scheduled')
			return (registeredFilter && scheduledFilter) || (!registeredFilter && !scheduledFilter)
				? 'schedregist'
				: registeredFilter
				? 'registered'
				: 'scheduled'
		},
	},
}
</script>

<style lang="scss">
.agenda-card {
	&-wrapper {
		background: #ffffff;
		border-radius: 8px;
		position: relative;
		margin-bottom: 8px;
		border: 1px solid #e2e2e2;
		&__time-start {
			position: absolute;
			top: 0;
			left: 0;
			width: 52px;
			height: 20px;
			background-color: #0055a3;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 12px;
			line-height: 16px;
			letter-spacing: 0;
			color: #ffffff;
			display: flex;
			align-items: center;
			justify-content: center;
			border-radius: 6px 0 0 0;
			clip-path: polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 8px) 100%, 0 100%);
		}
	}
	&-seats {
		width: 100px;
		height: 24px;
		background-color: #0055a3;
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 13px;
		line-height: 140%;
		letter-spacing: 0;
		color: #ffffff;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 20px;
		margin-top: 8px;
	}

	&-date {
		color: var(--accent-color);
		font-size: 13px;
		flex-shrink: 0;
		min-width: 52px;
		&__begin {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 140%;
			letter-spacing: 0;
		}

		&__end {
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 140%;
			letter-spacing: 0;
		}
		&__circle {
			margin-top: 8px;
			width: 6px;
			height: 6px;
			background-color: #674ea6;
			gap: 6px;
			border-radius: 50%;
		}
		&__circle-wrapper {
			display: flex;
			flex-wrap: wrap;
			gap: 4px;
			max-width: calc((6px * 5) + (4px * 4));
		}
	}

	&-description {
		color: var(--accent-color);
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 16px;
		line-height: 140%;
		letter-spacing: 0;
	}

	&-tag {
		display: flex;
		align-items: center;
		font-size: 13px;
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		line-height: 19.37px;

		p {
			word-break: break-all;
		}
	}

	&-location {
		display: flex;
		font-family: 'Inter', sans-serif;
		font-weight: 500;
		font-size: 13px;
		line-height: 140%;
		letter-spacing: 0;
	}

	&-schedule {
		&-btn {
			background-color: transparent !important;
			padding: 0 8px !important;
			min-width: auto !important;
		}
	}
}
</style>
